import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { MARKET_NAMES } from "./format";
import { MarketProvider, SessionProvider, useMarket, useSession, useTheme } from "./session";
import { ToastProvider } from "./ui/toast";
import { Dialog } from "./ui/dialog";
import { Loading } from "./ui/bits";
import { CommandPalette } from "./ui/cmdk";
import { Lockup } from "./ui/brand";
import {
  Activity, KeyRound, Briefcase, CheckCheck, ClipboardList, Coins, CreditCard, FileText, Gauge, Gift, Keyboard, LayoutDashboard, LifeBuoy, Lock, MessageSquareText, LogOut, Map as MapIcon, Megaphone,
  Menu, MessagesSquare, Mic, Moon, ScrollText, Search, ShieldAlert, ShieldCheck, SlidersHorizontal, Store, Sun, SunMoon, ToggleRight, TrendingUp, UserCog, Users, type LucideIcon,
  Sparkles, BookOpen, BookOpenText, Clapperboard, CloudSun, Dices, GraduationCap, Handshake, Landmark, Terminal, Music, Radio as RadioIcon,
} from "lucide-react";
import { SignIn } from "./pages/SignIn";
import { AcceptInvite } from "./pages/AcceptInvite";

const Overview = lazy(() => import("./pages/Overview"));
const Players = lazy(() => import("./pages/Players"));
const PlayerProfile = lazy(() => import("./pages/PlayerProfile"));
const Approvals = lazy(() => import("./pages/Approvals"));
const Finance = lazy(() => import("./pages/Finance"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const Economy = lazy(() => import("./pages/Economy"));
const Careers = lazy(() => import("./pages/Careers"));
const Games = lazy(() => import("./pages/Games"));
const Businesses = lazy(() => import("./pages/Businesses"));
const Signals = lazy(() => import("./pages/Signals"));
const LiveOps = lazy(() => import("./pages/LiveOps"));
const Content = lazy(() => import("./pages/Content"));
const Story = lazy(() => import("./pages/Story"));
const Courses = lazy(() => import("./pages/Courses"));
const Director = lazy(() => import("./pages/Director"));
const Social = lazy(() => import("./pages/Social"));
const Ops = lazy(() => import("./pages/Ops"));
const UGC = lazy(() => import("./pages/UGC"));
const Fraud = lazy(() => import("./pages/Fraud"));
const ClusterDetail = lazy(() => import("./pages/DeviceGraph").then((m) => ({ default: m.ClusterDetail })));
const Support = lazy(() => import("./pages/Support"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Privacy = lazy(() => import("./pages/Privacy"));
const SpendReviews = lazy(() => import("./pages/SpendReviews"));
const Compensation = lazy(() => import("./pages/Compensation"));
const Ads = lazy(() => import("./pages/Ads"));
const AdsCampaign = lazy(() => import("./pages/Ads").then((m) => ({ default: m.CampaignPage })));
const AdsPartner = lazy(() => import("./pages/Ads").then((m) => ({ default: m.PartnerPage })));
const AdsBilling = lazy(() => import("./pages/Ads").then((m) => ({ default: m.BillingPage })));
const SponsorshipRedirect = lazy(() => import("./pages/Ads").then((m) => ({ default: m.SponsorshipRedirect })));
const World = lazy(() => import("./pages/World"));
const Rooms = lazy(() => import("./pages/Rooms"));
const Studio = lazy(() => import("./pages/Studio"));
const Radio = lazy(() => import("./pages/Radio"));
const Growth = lazy(() => import("./pages/Growth"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const SystemPage = lazy(() => import("./pages/System"));
const Team = lazy(() => import("./pages/Team"));
const Audit = lazy(() => import("./pages/Audit"));
const Logs = lazy(() => import("./pages/Logs"));
const Security = lazy(() => import("./pages/Security"));
const Assistant = lazy(() => import("./pages/Assistant"));
const Manual = lazy(() => import("./pages/Manual"));

export function App() {
  return (
    <ToastProvider>
      <SessionProvider>
        <Routes>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route
            path="/*"
            element={
              <RequireSession>
                <MarketProvider>
                  <Shell />
                </MarketProvider>
              </RequireSession>
            }
          />
        </Routes>
      </SessionProvider>
    </ToastProvider>
  );
}

function RequireSession({ children }: { children: ReactNode }) {
  const { token, me, loading, expired } = useSession();
  const loc = useLocation();
  if (!token) return <Navigate to={"/sign-in?" + (expired ? "expired=1&" : "") + "next=" + encodeURIComponent(loc.pathname + loc.search)} replace />;
  if (loading || !me) return <Loading label="Loading…" />;
  return <>{children}</>;
}

// ---------------------------------------------------------------- navigation

export interface NavItem {
  to: string;
  label: string;
  perms: string[] | null; // any of; null = everyone
  end?: boolean;
  key?: string; // g+key shortcut
  icon: LucideIcon;
  hint?: string; // extra words the command palette matches
  /** Sections the command palette lists too (perms: any of; omitted = the item's). */
  subs?: { to: string; label: string; perms?: string[]; hint?: string }[];
}

export const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: "Operate",
    items: [
      { to: "/", label: "Overview", perms: ["overview.read"], end: true, key: "o", icon: LayoutDashboard, hint: "home dashboard today" },
      { to: "/players", label: "Players", perms: ["players.read"], key: "p", icon: Users, hint: "accounts search" },
      { to: "/approvals", label: "Approvals", perms: null, key: "a", icon: CheckCheck, hint: "four eyes pending" },
      { to: "/assistant", label: "Assistant", perms: ["assistant.use"], icon: Sparkles, hint: "ask natural language plain words chat ai configure switches holidays" },
      { to: "/support", label: "Support tickets", perms: ["support.read", "payments.full"], key: "t", icon: LifeBuoy, hint: "help desk" },
      { to: "/feedback", label: "Player feedback", perms: ["support.read"], key: "b", icon: MessageSquareText, hint: "bugs ideas screenshots suggestions" },
      { to: "/spend-reviews", label: "Spend reviews", perms: ["players.write"], icon: Gauge, hint: "limits caps" },
      { to: "/privacy", label: "Privacy requests", perms: ["privacy.read"], icon: Lock, hint: "export delete dsar" },
      { to: "/growth", label: "Growth", perms: ["analytics.read"], icon: TrendingUp, hint: "funnel retention" },
      { to: "/waitlist", label: "Waitlist & leads", perms: ["analytics.read"], icon: ClipboardList, hint: "signups site forms" },
    ],
  },
  {
    group: "Money",
    items: [
      { to: "/finance", label: "Payments & finance", perms: ["payments.read", "finance.recon"], key: "f", icon: CreditCard, hint: "orders refunds revenue ledger" },
      { to: "/economy", label: "Economy", perms: ["economy.read"], key: "e", icon: Coins, hint: "catalogue prices free play" },
      { to: "/careers", label: "Careers", perms: ["economy.read"], icon: Briefcase, hint: "jobs work employers ladders shifts wages payslips" },
      { to: "/businesses", label: "Player businesses", perms: ["economy.read"], icon: Store, hint: "biz shops treasury rent arrears npc demand override ugc:biz" },
      { to: "/economy/biz", label: "Business & P2P dials", perms: ["economy.read"], icon: SlidersHorizontal, hint: "npc demand hour curve categories rent arrears premises taxes payroll dividend stamp duty p2p caps receive tiers clearing payback ecn-14a" },
      { to: "/economy/tuning", label: "World tuning", perms: ["economy.read"], icon: SlidersHorizontal, hint: "arms weapon ammo prices mission incident service rewards pct xp festival holiday bonus standing day cap limits ceilings adr 0059" },
      { to: "/economy/civic", label: "Civic projects", perms: ["economy.read"], icon: Landmark, hint: "city goals taxes civic_pct landmark district upgrade festival unlock ecn-08a ugc:civic" },
      { to: "/compensation", label: "Compensation & inbox", perms: ["compensation.bulk", "messaging.send"], icon: Gift, hint: "grants messages" },
      {
        to: "/ads",
        label: "Ads",
        perms: ["sponsorship.read", "partners.review", "ads.billing"],
        key: "d",
        icon: Megaphone,
        hint: "sponsorship sponsored advertising brands",
        subs: [
          { to: "/ads", label: "Ads overview", perms: ["sponsorship.read"], hint: "revenue impressions reach" },
          { to: "/ads/partners", label: "Ad partners", perms: ["partners.review"], hint: "verify reject suspend advertisers merchants" },
          { to: "/ads/campaigns", label: "Ad campaigns", perms: ["sponsorship.read"], hint: "pacing status" },
          { to: "/ads/review/campaigns", label: "Campaign review", perms: ["sponsorship.read"], hint: "approve reject four eyes" },
          { to: "/ads/review/creatives", label: "Creative review", perms: ["sponsorship.read"], hint: "approve reject artwork" },
          { to: "/ads/rate-cards", label: "Rate cards", perms: ["sponsorship.read"], hint: "cpm cpc prices" },
          { to: "/ads/billing", label: "Partner billing", perms: ["ads.billing", "sponsorship.write"], hint: "balances ledger top-ups invoices adjust" },
          { to: "/ads/placements", label: "Ad placements", perms: ["sponsorship.read"], hint: "inventory slots formats preview" },
        ],
      },
    ],
  },
  {
    group: "Game",
    items: [
      { to: "/liveops", label: "Live-ops", perms: ["liveops.read", "killswitch"], key: "l", icon: ToggleRight, hint: "kill switch flags maintenance banners" },
      { to: "/signals", label: "Real-world signals", perms: ["liveops.read"], icon: CloudSun, hint: "weather holidays fixtures football watch party provider" },
      { to: "/world", label: "World live-ops", perms: ["liveops.read"], key: "w", icon: MapIcon, hint: "cities events" },
      { to: "/content", label: "Content CMS", perms: ["content.read"], key: "c", icon: FileText, hint: "copy lines" },
      { to: "/story", label: "Story", perms: ["content.read"], icon: BookOpen, hint: "story campaign come-up chapters beats acts characters scenes choices graph playthrough draft publish kill:story" },
      { to: "/courses", label: "Courses", perms: ["content.read", "fraud.read"], icon: GraduationCap, hint: "micro-courses skills lessons quiz certificates pass rates questions held quizzes quiz_fast clear void accuracy publish kill:courses" },
      {
        to: "/director",
        label: "World Director",
        // Content reviewers (content.*) reach the review queue only.
        perms: ["liveops.read", "content.read"],
        icon: Clapperboard,
        hint: "director world arcs city arcs contracts district vibe rotation envelope proposals approve reject timeline why dry run today hooks holdout pause kill:director",
        subs: [{ to: "/director?t=content", label: "Generated content review", perms: ["content.read"], hint: "ai city stories seasons world memory approve reject take down overturn level" }],
      },
      { to: "/social", label: "Social & moderation", perms: ["social.read"], key: "s", icon: MessagesSquare, hint: "reports chat" },
      { to: "/games", label: "Table games", perms: ["games.config"], icon: Dices, hint: "connect 4 tic-tac-toe chess checkers draughts mancala oware ludo dominoes whot backgammon gomoku five in a row snakes and ladders reversi othello spar last card kadi drop four arena spades rummy poker holdem blackjack casino cards board games life-sized tables modes casual rewarded ranked rewards daily cap xp ratings" },
      { to: "/ops", label: "Co-op ops", perms: ["social.read"], icon: Handshake, hint: "ops missions contracts runs takes heist lobby allowance cancel run fast answers kill:ops" },
      {
        to: "/ugc",
        label: "UGC moderation",
        perms: ["ugc.review", "ugc.editorial"],
        icon: ShieldCheck,
        hint: "user content queue reports strikes appeals reserved names trademarks business brands crew names designs events posts reviews city times",
        subs: [
          { to: "/ugc/queue", label: "UGC queue", perms: ["ugc.review"], hint: "approve reject dismiss held reported urgent" },
          { to: "/ugc/events", label: "Player events", perms: ["ugc.review"], hint: "events hosts rsvps check-ins venues audio" },
          { to: "/ugc/posts", label: "City posts", perms: ["ugc.review"], hint: "feed posts hiring drops" },
          { to: "/ugc/reviews", label: "Reviews", perms: ["ugc.review"], hint: "stars employer reviews owner flagged replies weight" },
          { to: "/ugc/strikes", label: "Strikes", perms: ["ugc.review"], hint: "issue void warn mute" },
          { to: "/ugc/appeals", label: "Appeals", perms: ["ugc.review"], hint: "uphold overturn" },
          { to: "/ugc/reserved", label: "Reserved names", perms: ["ugc.review"], hint: "trademarks public figures slurs partner only" },
          { to: "/ugc/designs", label: "Designs", perms: ["ugc.review"], hint: "designed goods dsn outfits decor menu items staff pick featured slot designers" },
          { to: "/ugc/times", label: "City Times", perms: ["ugc.editorial"], hint: "newspaper edition picks headline drop rebuild editorial ugc:times" },
          { to: "/ugc/contests", label: "Contests", perms: ["ugc.editorial"], hint: "design contests themes entries votes winners seasonal independence day detty december eid creator chart employee of the month" },
        ],
      },
      { to: "/rooms", label: "Audio rooms", perms: ["social.read"], icon: Mic, hint: "livekit radio" },
      {
        to: "/studio",
        label: "Music studio",
        perms: ["music.read"],
        key: "i",
        icon: Music,
        hint: "music songs tracks lyrics suno ai generate takes audition stinger loop jingle bed playlist placement cue score soundtrack radio engine budget publish",
        subs: [
          { to: "/studio/create", label: "Make a track", perms: ["music.write"], hint: "brief generate lyrics songwriter candidates" },
          { to: "/studio/playlists", label: "Playlists", perms: ["music.read"], hint: "radio bed mix venue" },
          { to: "/studio/places", label: "Places & cues", perms: ["music.read"], hint: "placements landmark district vehicle car radio stinger story beat" },
          { to: "/studio/budget", label: "Music budget", perms: ["music.read"], hint: "spend cap engine elevenlabs" },
        ],
      },
      {
        to: "/radio",
        label: "World radio",
        perms: ["radio.read"],
        key: "n",
        icon: RadioIcon,
        hint: "radio station dj news bulletin newsdesk on air queue skip veto announce persona voice elevenlabs openai google tts guardrail kill:radio feature:radio",
        subs: [
          { to: "/radio/news", label: "Newsdesk", perms: ["radio.read"], hint: "bulletins links station ids facts pull veto" },
          { to: "/radio/djs", label: "DJs", perms: ["radio.read"], hint: "personas voices" },
          { to: "/radio/station", label: "Radio station settings", perms: ["radio.read"], hint: "veto window switch pause budget" },
        ],
      },
    ],
  },
  {
    group: "Risk & admin",
    items: [
      { to: "/fraud", label: "Fraud & risk", perms: ["fraud.read"], key: "r", icon: ShieldAlert, hint: "device graph clusters signals" },
      { to: "/system", label: "System", perms: ["system.read"], key: "y", icon: Activity, hint: "health jobs" },
      { to: "/team", label: "Team & access", perms: ["team.read"], key: "m", icon: UserCog, hint: "staff roles permissions" },
      { to: "/audit", label: "Audit log", perms: ["audit.read"], key: "u", icon: ScrollText, hint: "history chain" },
      { to: "/logs", label: "Logs", perms: ["logs.read"], key: "j", icon: Terminal, hint: "events requests telemetry troubleshooting api platform" },
      // The Operator Manual (content/guides/operator-manual.md): every staff role. The palette also finds its sections.
      { to: "/manual", label: "Operator manual", perms: null, key: "h", icon: BookOpenText, hint: "help guide handbook how to docs runbook bible roles start here" },
    ],
  },
];

const initials = (email: string) => email.split("@")[0].split(/[._-]/).filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?";

function Shell() {
  const { me, can, signOut } = useSession();
  const { market, setMarket, markets } = useMarket();
  const [theme, setTheme] = useTheme();
  const [navOpen, setNavOpen] = useState(false);
  const [help, setHelp] = useState(false);
  const [palette, setPalette] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  const allowed = (i: NavItem) => i.perms === null || i.perms.some((p) => can(p));
  const nav = useMemo(
    () =>
      NAV.map((g) => ({
        ...g,
        items: g.items.filter(allowed).map((i) => ({ ...i, subs: i.subs?.filter((x) => !x.perms || x.perms.some((p) => can(p))) })),
      })).filter((g) => g.items.length),
    [me], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const approvalsQ = useQuery({ queryKey: ["approvals", "pending"], queryFn: () => api.approvals("pending"), refetchInterval: 30_000 });
  const waiting = (approvalsQ.data?.approvals ?? []).filter((a) => !a.mine).length;

  useEffect(() => setNavOpen(false), [loc.pathname]);
  const env = me?.env || "dev";
  useEffect(() => {
    document.documentElement.dataset.env = env;
  }, [env]);

  // Keyboard: Cmd/Ctrl+K opens the palette, "/" focuses the page search, "g" then a letter jumps, "?" shows help.
  useEffect(() => {
    let g = false;
    let gTimer: number | undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((v) => !v);
        return;
      }
      const t = e.target as HTMLElement;
      if (t.closest("input, textarea, select, [contenteditable=true]") || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector("dialog[open]")) return;
      if (e.key === "/") {
        const s = document.querySelector<HTMLElement>("[data-search]");
        e.preventDefault();
        if (s) s.focus();
        else setPalette(true);
      } else if (e.key === "?") {
        setHelp(true);
      } else if (g) {
        g = false;
        const item = NAV.flatMap((x) => x.items).find((i) => i.key === e.key);
        if (item && allowed(item)) navigate(item.to);
      } else if (e.key === "g") {
        g = true;
        window.clearTimeout(gTimer);
        gTimer = window.setTimeout(() => (g = false), 1200);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps

  const roles = Object.entries(me?.staff.roles ?? {}).map(([r, m]) => r + (m.length ? " (" + m.join(", ") + ")" : "")).join(", ");
  const nextTheme = theme === "dark" ? "light" : theme === "light" ? "system" : "dark";
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <div className={"shell" + (navOpen ? " nav-open" : "")}>
      <nav className="nav" aria-label="Console">
        <Link to="/" className="brand">
          <Lockup label="staff" />
        </Link>
        <span className="env-chip" title="Environment" data-testid="env-badge">
          {env}
        </span>
        <div className="nav-scroll">
          {nav.map((g) => (
            <div className="nav-group" key={g.group}>
              <h3>{g.group}</h3>
              {g.items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.end} className={({ isActive }) => (isActive ? "active" : "")}>
                  <i.icon aria-hidden="true" />
                  <span className="lbl">{i.label}</span>
                  {i.to === "/approvals" && waiting > 0 && <span className="count">{waiting}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
        <div className="nav-foot">
          <span className="initials" aria-hidden="true">
            {initials(me?.staff.email ?? "")}
          </span>
          <span className="who-name" title={roles}>
            {me?.staff.email}
            <small>{Object.keys(me?.staff.roles ?? {}).join(", ") || "staff"}</small>
          </span>
          <button className="ghost icon-btn" onClick={() => setHelp(true)} aria-label="Keyboard shortcuts" title="Keyboard shortcuts (?)">
            <Keyboard />
          </button>
          <NavLink to="/security" className="ghost icon-btn" aria-label="Security and passkeys" title="Security and passkeys">
            <KeyRound />
          </NavLink>
          <button className="ghost icon-btn" onClick={() => void signOut()} aria-label="Sign out" title="Sign out">
            <LogOut />
          </button>
        </div>
      </nav>
      <header className="topbar">
        <button className="ghost nav-toggle icon-btn" aria-label="Menu" onClick={() => setNavOpen((v) => !v)}>
          <Menu />
        </button>
        <button className="cmdk-trigger" onClick={() => setPalette(true)} aria-label="Search pages and players" data-testid="cmdk-open">
          <Search aria-hidden="true" />
          <span className="grow">Search players, pages…</span>
          <kbd className="hint-k">{isMac ? "⌘" : "Ctrl"} K</kbd>
        </button>
        <div className="spacer" />
        <label className="row" title="Filters lists and dashboards by market">
          <span className="sr-only">Market</span>
          <select className="mkt-select" value={market} onChange={(e) => setMarket(e.target.value)} aria-label="Market filter" data-testid="market-filter">
            {markets.length > 1 && <option value="">All markets</option>}
            {markets.map((m) => (
              <option key={m} value={m}>
                {m} · {MARKET_NAMES[m]}
              </option>
            ))}
          </select>
        </label>
        <button className="attn-pill" onClick={() => navigate("/approvals")} title="Approvals waiting for you" aria-label={waiting > 0 ? `Approvals, ${waiting} waiting` : "Approvals"} data-testid="approvals-pill">
          <CheckCheck aria-hidden="true" />
          <span className="attn-label">Approvals</span> {waiting > 0 && <span className="n">{waiting}</span>}
        </button>
        <button
          className="ghost icon-btn"
          onClick={() => setTheme(nextTheme)}
          aria-label={"Theme: " + theme + ". Switch to " + nextTheme}
          title={"Theme: " + theme + " (click for " + nextTheme + ")"}
        >
          {theme === "dark" ? <Moon /> : theme === "light" ? <Sun /> : <SunMoon />}
        </button>
      </header>
      <main className="main" id="main">
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route index element={can("overview.read") ? <Overview /> : <Navigate to={nav[0]?.items[0]?.to ?? "/approvals"} replace />} />
            <Route path="players" element={<Players />} />
            <Route path="players/:id/:tab?" element={<PlayerProfile />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="assistant" element={<Assistant />} />
            <Route path="finance/orders/:id" element={<OrderDetail />} />
            <Route path="finance/:tab?" element={<Finance />} />
            <Route path="economy/:tab?" element={<Economy />} />
            <Route path="careers" element={<Careers />} />
            <Route path="businesses" element={<Businesses />} />
            <Route path="signals" element={<Signals />} />
            <Route path="liveops/:tab?" element={<LiveOps />} />
            <Route path="content" element={<Content />} />
            <Route path="story" element={<Story />} />
            <Route path="courses" element={<Courses />} />
            <Route path="director" element={<Director />} />
            <Route path="social/:tab?" element={<Social />} />
            <Route path="ops" element={<Ops />} />
            <Route path="games" element={<Games />} />
            <Route path="ugc/:tab?" element={<UGC />} />
            <Route path="fraud/clusters/:root" element={<ClusterDetail />} />
            <Route path="fraud/:tab?" element={<Fraud />} />
            <Route path="support" element={<Support />} />
            <Route path="feedback" element={<Feedback />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="spend-reviews" element={<SpendReviews />} />
            <Route path="compensation" element={<Compensation />} />
            <Route path="ads/campaigns/:id/:section?" element={<AdsCampaign />} />
            <Route path="ads/partners/:id" element={<AdsPartner />} />
            <Route path="ads/billing/:id" element={<AdsBilling />} />
            <Route path="ads/:tab?/:sub?" element={<Ads />} />
            <Route path="sponsorship/*" element={<SponsorshipRedirect />} />
            <Route path="world/:tab?" element={<World />} />
            <Route path="rooms/:tab?" element={<Rooms />} />
            <Route path="studio/:tab?" element={<Studio />} />
            <Route path="radio/:tab?" element={<Radio />} />
            <Route path="growth" element={<Growth />} />
            <Route path="waitlist" element={<Waitlist />} />
            <Route path="system" element={<SystemPage />} />
            <Route path="team/:tab?" element={<Team />} />
            <Route path="audit" element={<Audit />} />
            <Route path="logs" element={<Logs />} />
            <Route path="security" element={<Security />} />
            <Route path="manual" element={<Manual />} />
            <Route path="*" element={<div className="page"><div className="page-head"><div className="titles"><h1>Page not found</h1><div className="sub">No page at this address. Search with {isMac ? "⌘" : "Ctrl"} K or pick a page on the left.</div></div></div></div>} />
          </Routes>
        </Suspense>
      </main>
      <CommandPalette open={palette} onClose={() => setPalette(false)} nav={nav} />
      <Dialog open={help} onClose={() => setHelp(false)} title="Keyboard shortcuts">
        <table className="shortcuts">
          <tbody>
            <tr><td><kbd>{isMac ? "⌘" : "Ctrl"}</kbd> <kbd>K</kbd></td><td>Search players and pages</td></tr>
            <tr><td><kbd>/</kbd></td><td>Focus the search on this page</td></tr>
            <tr><td><kbd>↑</kbd> <kbd>↓</kbd> or <kbd>j</kbd> <kbd>k</kbd></td><td>Move between table rows</td></tr>
            <tr><td><kbd>Enter</kbd></td><td>Open the focused row</td></tr>
            <tr><td><kbd>Esc</kbd></td><td>Close a dialog</td></tr>
            <tr><td><kbd>Ctrl</kbd>+<kbd>Enter</kbd></td><td>Submit a reason dialog</td></tr>
            {NAV.flatMap((g) => g.items)
              .filter((i) => i.key && allowed(i))
              .map((i) => (
                <tr key={i.to}>
                  <td>
                    <kbd>g</kbd> <kbd>{i.key}</kbd>
                  </td>
                  <td>{i.label}</td>
                </tr>
              ))}
          </tbody>
        </table>
        <p className="muted" style={{ marginTop: 12 }}>
          How each page works, who may do what, and what to do when something breaks:{" "}
          <Link to="/manual" onClick={() => setHelp(false)} data-testid="help-manual">
            the Operator manual
          </Link>
          .
        </p>
      </Dialog>
    </div>
  );
}
